import './scss/player-profile.scss';
import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useParams, useHistory } from 'react-router-dom';
import { resolveCharacter, resolveReincarnation } from './historyData';

const numberFormat = (v) => new Intl.NumberFormat('en-US').format(Number(v) || 0);
const formatDuration = (sec) => {
  const total = Number(sec) || 0;
  const m = Math.floor(total / 60);
  const s = Math.floor(total % 60)
    .toString()
    .padStart(2, '0');
  return `${m}m${s !== '00' ? ` ${s}s` : ''}`;
};

const resolveRankBadge = (rankedPoint, absoluteRank = null) => {
  if (absoluteRank === 1) return { src: '/Img/ranking/master.png', label: 'Master' };
  if (rankedPoint >= 350) return { src: '/Img/ranking/master.png', label: 'Master' };
  if (rankedPoint >= 250) return { src: '/Img/ranking/emeraude.png', label: 'Emeraude' };
  if (rankedPoint >= 150) return { src: '/Img/ranking/gold.png', label: 'Gold' };
  if (rankedPoint >= 50) return { src: '/Img/ranking/silver.png', label: 'Silver' };
  return { src: '/Img/ranking/bronze.png', label: 'Bronze' };
};

function PlayerProfile() {
  const { userId } = useParams();
  const history = useHistory();
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [profile, setProfile] = useState(null);
  const [activeMode, setActiveMode] = useState('all');

  const fetchProfile = async (payload) => {
    setLoading(true);
    try {
      const { data } = await axios.get('https://rivandy.com:3003/game/GetPlayerStats', { params: payload });
      setProfile(data || {});
      setError(null);
      if (payload.userId) history.replace(`/player/${payload.userId}`);
    } catch (e) {
      setProfile(null);
      setError('Unable to load player profile.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (userId) {
      fetchProfile({ userId });
    }
  }, [userId]);

  const selectedMode = useMemo(() => {
    if (!profile) return null;
    if (activeMode === 'all') return profile.totals;
    const found = (profile.modes || []).find((m) => (m.mode || '').toLowerCase() === activeMode.toLowerCase());
    return found || profile.totals;
  }, [profile, activeMode]);

  const recentGames = profile?.recentGames || [];

  const avatarUrl = useMemo(() => {
    const p = profile?.player || {};
    const candidates = [p.avatar, p.avatarId, p.avatarGame, p.steamprofile];
    return candidates.find((src) => typeof src === 'string' && src.toLowerCase().startsWith('http')) || null;
  }, [profile]);

  const rankedPoint = Number(profile?.player?.rankedPoint) || 0;
  const rankBadge = resolveRankBadge(rankedPoint, profile?.player?.rankPosition);

  const handleSearch = (e) => {
    e.preventDefault();
    if (!query.trim()) return;
    fetchProfile({ playerName: query.trim() });
  };

  return (
    <section className="profilePage separator">
      <header className="profile-header">
        <div>
          <p className="eyebrow">Player profile</p>
          <h1>Match insights</h1>
          <p className="subhead">Search a player to view their performance in Battle Royale and Survivor modes.</p>
        </div>
        <form className="profile-search" onSubmit={handleSearch}>
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search by username..."
          />
          <button type="submit">Search</button>
        </form>
      </header>

      {loading && <div className="info-banner">Loading profile...</div>}
      {error && <div className="info-banner error">{error}</div>}

      {profile && (
        <>
          <section className="profile-top">
            <div className="profile-card hero">
              <div className="hero-grid">
                <div className="hero-left">
                  <div className="avatar-shell">
                    {avatarUrl ? <img src={avatarUrl} alt={profile.player?.username || 'avatar'} /> : <div className="avatar-fallback">?</div>}
                    <div className="badge">{Math.round(profile.totals?.winRate || 0)}% WR</div>
                  </div>
                  <div>
                    <h2>{profile.player?.username || 'Unknown player'}</h2>
                    <p className="muted">Level {profile.player?.level ?? 0}</p>
                    <p className="muted tiny">Account #{profile.player?.accountId || '-'}</p>
                    {profile.player?.steamprofile && (
                      <a className="link-inline small-link" href={profile.player.steamprofile} target="_blank" rel="noreferrer">
                        Steam profile
                      </a>
                    )}
                    <p className="muted tiny">Last game: {profile.totals?.lastGame ? new Date(profile.totals.lastGame).toLocaleDateString('en-US') : '-'}</p>
                  </div>
                </div>
                <div className="hero-rank">
                  <div className="rank-badge-shell">
                    {rankBadge ? <img src={rankBadge.src} alt={rankBadge.label} /> : null}
                  </div>
                  <div className="rank-meta">
                    <p className="muted tiny">Ranking Points</p>
                    <h1>{numberFormat(rankedPoint)}</h1>
                    <p className="muted small">{rankBadge?.label || 'Unranked'}{profile.player?.rankPosition ? ` · Global #${numberFormat(profile.player.rankPosition)}` : ''}</p>
                  </div>
                </div>
                <div className="hero-stats">
                  <div>
                    <span className="muted tiny">Matches</span>
                    <strong>{numberFormat(profile.totals?.games)}</strong>
                  </div>
                  <div>
                    <span className="muted tiny">Wins</span>
                    <strong>{numberFormat(profile.totals?.wins)} ({Math.round(profile.totals?.winRate || 0)}%)</strong>
                  </div>
                  <div>
                    <span className="muted tiny">Avg duration</span>
                    <strong>{formatDuration(profile.totals?.avgDuration)}</strong>
                  </div>
                  <div>
                    <span className="muted tiny">Last match</span>
                    <strong>{profile.totals?.lastGame ? new Date(profile.totals.lastGame).toLocaleDateString('en-US') : '-'}</strong>
                  </div>
                </div>
              </div>
            </div>

            <div className="mode-switch">
              {['all', 'Survivor', 'BattleRoyale'].map((mode) => (
                <button key={mode} className={`switch-btn ${activeMode === mode ? 'is-active' : ''}`} onClick={() => setActiveMode(mode)}>
                  {mode === 'all' ? 'All modes' : mode}
                </button>
              ))}
            </div>

            <div className="profile-summary">
              <article className="summary-card">
                <p className="muted tiny">Kills</p>
                <h3>{numberFormat(selectedMode?.kills)}</h3>
                <p className="muted tiny">Monsters: {numberFormat(selectedMode?.monsterKills)}</p>
              </article>
              <article className="summary-card">
                <p className="muted tiny">Win rate</p>
                <h3>{Math.round(selectedMode?.winRate || 0)}%</h3>
                <p className="muted tiny">{numberFormat(selectedMode?.wins)} wins</p>
              </article>
              <article className="summary-card">
                <p className="muted tiny">Matches</p>
                <h3>{numberFormat(selectedMode?.games)}</h3>
                <p className="muted tiny">Avg duration {formatDuration(selectedMode?.avgDuration)}</p>
              </article>
            </div>
          </section>

          <section className="profile-modes">
            {(profile.modes || []).map((m) => (
              <div key={m.mode || 'unknown'} className="mode-card">
                <header>
                  <span className="pill mode-pill">{m.mode || 'Mode'}</span>
                  <strong>{numberFormat(m.games)} matches</strong>
                </header>
                <p className="muted small">{Math.round(m.winRate || 0)}% win rate</p>
                <div className="mode-grid">
                  <div><span className="muted tiny">Wins</span><strong>{numberFormat(m.wins)}</strong></div>
                  <div><span className="muted tiny">Kills</span><strong>{numberFormat(m.kills)}</strong></div>
                  <div><span className="muted tiny">Monsters</span><strong>{numberFormat(m.monsterKills)}</strong></div>
                  <div><span className="muted tiny">Avg duration</span><strong>{formatDuration(m.avgDuration)}</strong></div>
                </div>
              </div>
            ))}
          </section>

          <section className="recent-games">
            <header>
              <h3>Recent matches</h3>
              <p className="muted small">Last {recentGames.length} games</p>
            </header>
            {recentGames.length === 0 && <div className="info-banner">No matches found for this player.</div>}
            <div className="recent-list">
              {recentGames.map((g) => {
                const isWin = g.winnerId != null && Number(g.winnerId) === Number(profile.player?.userId);
                const character = resolveCharacter(Number(g.classId));
                const spirit = resolveReincarnation(g.reId != null ? Number(g.reId) : undefined);
                return (
                  <article key={`${g.gameID}`} className={`recent-card ${isWin ? 'is-win' : ''}`}>
                    <div className="recent-top">
                      <span className="pill mode-pill">{g.gameMode || 'Mode'}</span>
                      <span className="muted tiny">{new Date(g.gameDateTime).toLocaleDateString('en-US')}</span>
                    </div>
                    <div className="recent-body">
                      <div className="recent-meta">
                        <p className="muted tiny">Map</p>
                        <strong>{g.mapName || 'Unknown'}</strong>
                        <p className="muted tiny">Duration {formatDuration(g.gameDuration)}</p>
                      </div>
                      <div className="recent-role">
                        {character && <img src={character.icon} alt={character.name} />}
                        <div>
                          <p className="label">{character ? character.name : '-'}</p>
                          <p className="muted tiny">{character ? character.characterType : ''}</p>
                        </div>
                      </div>
                      <div className="recent-role">
                        {spirit && <img src={spirit.icon} alt={spirit.name} />}
                        <div>
                          <p className="label">{spirit ? spirit.name : '-'}</p>
                          <p className="muted tiny">{spirit ? spirit.characterType : ''}</p>
                        </div>
                      </div>
                      <div className="recent-stats">
                        <div><span className="muted tiny">Kills</span><strong>{g.kills ?? 0}</strong></div>
                        <div><span className="muted tiny">Monsters</span><strong>{g.monster_kills ?? g.monsterKills ?? 0}</strong></div>
                        <div><span className="muted tiny">Wave</span><strong>{g.waveNumber ?? '-'}</strong></div>
                      </div>
                    </div>
                  </article>
                );
              })}
            </div>
          </section>
        </>
      )}
    </section>
  );
}

export default PlayerProfile;
